import { createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import toast from "../hooks/toast";
import { buildApiV3Url } from "../shared/Utils";
import certificateFiltersData from "./certificateSlice/certificateFiltersData";
import customerFiltersData from "./customerSlice/customerFiltersData";

const initialState = {
    isLoading: false,
    expirationDate: null,
    addLabelsList: [],
    removeLabelsList: [],
    addInvalidReasonsList: [],
    removeInvalidReasonsList: [],
    selectedField: "",
    certificateIds: [],
    addCustomerLabelsList: [],
    removeCustomerLabelsList: [],
    customerIds: [],
};

export const bulkUpdateSlice = createSlice({
    name: "bulkUpdate",
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setExpirationDate: (state, action) => {
            state.expirationDate = action.payload;
        },
        setAddLabelsList: (state, action) => {
            state.addLabelsList = action.payload;
        },
        setRemoveLabelsList: (state, action) => {
            state.removeLabelsList = action.payload;
        },
        setAddInvalidReasonsList: (state, action) => {
            state.addInvalidReasonsList = action.payload;
        },
        setRemoveInvalidReasonsList: (state, action) => {
            state.removeInvalidReasonsList = action.payload;
        },
        setSelectedField: (state, action) => {
            state.selectedField = action.payload;
        },
        setCertificateIds: (state, action) => {
            state.certificateIds = action.payload;
        },
        setAddCustomerLabelsList: (state, action) => {
            state.addCustomerLabelsList = action.payload;
        },
        setRemoveCustomerLabelsList: (state, action) => {
            state.removeCustomerLabelsList = action.payload;
        },
        setCustomerIds: (state, action) => {
            state.customerIds = action.payload;
        },
    },
});
export const {
    setIsLoading,
    setExpirationDate,
    setAddLabelsList,
    setRemoveLabelsList,
    setAddInvalidReasonsList,
    setRemoveInvalidReasonsList,
    setSelectedField,
    setCertificateIds,
    setAddCustomerLabelsList,
    setRemoveCustomerLabelsList,
    setCustomerIds,
} = bulkUpdateSlice.actions;

export const selectIsLoading = state => {
    return state.bulkUpdate.isLoading;
};
export const selectExpirationDate = state => {
    return state.bulkUpdate.expirationDate;
};
export const selectAddLabelsList = state => {
    return state.bulkUpdate.addLabelsList;
};
export const selectRemoveLabelsList = state => {
    return state.bulkUpdate.removeLabelsList;
};
export const selectAddInvalidReasonsList = state => {
    return state.bulkUpdate.addInvalidReasonsList;
};
export const selectRemoveInvalidReasonsList = state => {
    return state.bulkUpdate.removeInvalidReasonsList;
};
export const selectSelectedField = state => {
    return state.bulkUpdate.selectedField;
};
export const selectCertificateIds = state => {
    return state.bulkUpdate.certificateIds;
};
export const selectAddCustomerLabelsList = state => {
    return state.bulkUpdate.addCustomerLabelsList;
};
export const selectRemoveCustomerLabelsList = state => {
    return state.bulkUpdate.removeCustomerLabelsList;
};
export const selectCustomerIds = state => {
    return state.bulkUpdate.customerIds;
};

export const getSelectedCertificateIds = () => (dispatch, getState) => {
    const { certificateIds } = getState().bulkUpdate;
    return certificateIds;
};

const [showToast] = toast();
export const putBulkUpdateCertificatesExpirationDate =
    (pageSize, expirationDate) => async (dispatch, getState) => {
        const { isLoading } = getState().bulkUpdate;
        if (isLoading) return;
        dispatch(setIsLoading(true));
        const certificateFilterJson = certificateFiltersData([], getState, pageSize, 0, false);
        const bulkUpdateJson = {
            certificateExportModel: certificateFilterJson,
            expirationDate,
        };
        const response = await axios.put(
            buildApiV3Url(`certificate-bulk/expiration-date`),
            bulkUpdateJson,
            { withCredentials: true }
        );
        if (response?.status === 200 && response?.data) {
            showToast(
                "success",
                `We are working on updating your certificates. It will be ready shortly.`
            );
        }
        dispatch(setIsLoading(false));
    };

export const putBulkUpdateCertificatesLabels =
    (pageSize, addCertificateCodeIds, removeCertificateCodeIds) => async (dispatch, getState) => {
        const { isLoading } = getState().bulkUpdate;
        if (isLoading) return;
        dispatch(setIsLoading(true));
        const certificateFilterJson = certificateFiltersData([], getState, pageSize, 0, false);

        const bulkUpdateJson = {
            certificateExportModel: certificateFilterJson,
            addCertificateCodeIds,
            removeCertificateCodeIds,
        };
        const response = await axios.put(buildApiV3Url(`certificate-bulk/labels`), bulkUpdateJson, {
            withCredentials: true,
        });
        if (response?.status === 200 && response?.data) {
            showToast(
                "success",
                `We are working on updating your certificates. It will be ready shortly.`
            );
        }
        dispatch(setIsLoading(false));
    };

export const putBulkUpdateCertificatesInvalidReasons =
    (pageSize, addInvalidReasonIds, removeInvalidReasonIds) => async (dispatch, getState) => {
        const { isLoading } = getState().bulkUpdate;
        if (isLoading) return;
        dispatch(setIsLoading(true));
        const certificateFilterJson = certificateFiltersData([], getState, pageSize, 0, false);

        const bulkUpdateJson = {
            certificateExportModel: certificateFilterJson,
            addInvalidReasonIds,
            removeInvalidReasonIds,
        };
        const response = await axios.put(
            buildApiV3Url(`certificate-bulk/invalid-reasons`),
            bulkUpdateJson,
            { withCredentials: true }
        );
        if (response?.status === 200 && response?.data) {
            showToast(
                "success",
                `We are working on updating your certificates. It will be ready shortly.`
            );
        }

        dispatch(setIsLoading(false));
    };

export const putBulkUpdateCustomerLabels =
    (pageSize, addCustomerLabelIds, removeCustomerLabelIds) => async (dispatch, getState) => {
        const { isLoading } = getState().bulkUpdate;
        if (isLoading) return;
        dispatch(setIsLoading(true));

        const customerFilterJson = customerFiltersData([], getState, pageSize, 0);

        // Ensure all label IDs are integers
        const addCustomerLabelIdsInt = addCustomerLabelIds.map(id => Number(id));
        const removeCustomerLabelIdsInt = removeCustomerLabelIds.map(id => Number(id));

        const bulkUpdateJson = {
            customerExportModel: customerFilterJson,
            addCustomerCodeIds: addCustomerLabelIdsInt,
            removeCustomerCodeIds: removeCustomerLabelIdsInt,
        };

        const response = await axios.put(buildApiV3Url("bulk-customers/labels"), bulkUpdateJson, {
            withCredentials: true,
        });

        if (response?.status === 200 && response?.data) {
            showToast(
                "success",
                `We are working on updating your customers. It will be ready shortly.`
            );
        } else {
            showToast("error", "Something went wrong while updating the customer labels.");
        }
        dispatch(setIsLoading(false));
    };

export default bulkUpdateSlice.reducer;
